/**
 * Lead-capture dialogs for the catalogue flow (Phase 4, Jun 2026 — launch copy).
 *
 * Two Shadcn Dialog forms triggered from /catalogue:
 *   - PartnerBrandLeadDialog  (POST /api/lead/partner-brand)
 *   - ConsultationLeadDialog  (POST /api/lead/consultation)
 *
 * Simplified spec (per user pre-launch list):
 *   PartnerBrandLeadDialog: name (req), email (optional, no validation),
 *     product lines [name + qty + frequency], notes (optional), submit.
 *   ConsultationLeadDialog: name (optional), phone (req, country-aware),
 *     preferred date/time (req), notes (optional).
 *
 * NOTE: The "AnyBrand" lead dialog has been removed from the launch UI.
 *       It is still exported as an alias for backward-compat imports.
 */
import React, { useState } from "react";
import { Plus, Trash2, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PhoneInput, { isPhoneValid } from "@/cabinet/components/PhoneInput";
import { useLang } from "@/lib/LangContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

const FREQS = [
  { value: "one_time",  en: "One-time",        ka: "ერთჯერადი" },
  { value: "weekly",    en: "Weekly",          ka: "ყოველკვირეული" },
  { value: "biweekly",  en: "Every 2 weeks",   ka: "ორ კვირაში ერთხელ" },
  { value: "monthly",   en: "Monthly",         ka: "ყოველთვიური" },
];

function emptyLine() {
  return {
    product_name: "",
    quantity: 1,
    frequency: "monthly",
  };
}

async function postJSON(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || res.statusText || "Network error");
  }
  return res.json();
}

/**
 * Shared success panel — supports per-flow copy.
 */
function SuccessPanel({ onClose, lang, label, messageEn, messageKa }) {
  return (
    <div className="py-8 text-center" data-testid={`${label}-success`}>
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#A9B89A]/40">
        <CheckCircle2 className="h-7 w-7 text-[#0A4D8C]" />
      </div>
      <h3 className="font-display text-2xl font-bold text-[#05223D]">
        {lang === "ka" ? "გმადლობთ!" : "Thank you!"}
      </h3>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#465B70]">
        {lang === "ka" ? messageKa : messageEn}
      </p>
      <Button
        onClick={onClose}
        className="mt-6 rounded-xl bg-[#F25C05] hover:bg-[#d44e02]"
        data-testid={`${label}-success-close`}
      >
        {lang === "ka" ? "დახურვა" : "Close"}
      </Button>
    </div>
  );
}

/* ---------- Partner Brand / Product Request dialog ------------------------ */

function ProductLineEditor({ line, idx, onChange, onRemove, canRemove, lang, testIdBase }) {
  const update = (patch) => onChange({ ...line, ...patch });
  const L = (en, ka) => (lang === "ka" ? ka : en);

  return (
    <div
      className="space-y-3 rounded-2xl border border-[#0A4D8C]/12 bg-[#FDFBF7] p-4"
      data-testid={`${testIdBase}-line-${idx}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-[#F25C05]">
          {L("Product", "პროდუქტი")} {idx + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[#9b1c1c] hover:text-[#7a1414]"
            data-testid={`${testIdBase}-line-${idx}-remove`}
            aria-label={L("Remove product", "პროდუქტის წაშლა")}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor={`${testIdBase}-${idx}-name`}>
            {L("Product name", "სახელი")}
          </Label>
          <Input
            id={`${testIdBase}-${idx}-name`}
            value={line.product_name}
            onChange={(e) => update({ product_name: e.target.value })}
            placeholder={L("e.g. Royal Canin Maxi Adult 15kg", "მაგ. Royal Canin Maxi Adult 15kg")}
            data-testid={`${testIdBase}-${idx}-name`}
            className="h-11 rounded-xl"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label>{L("Quantity", "რაოდენობა")}</Label>
          <Input
            type="number"
            min="1"
            step="1"
            value={line.quantity}
            onChange={(e) => update({ quantity: Number(e.target.value) || 1 })}
            data-testid={`${testIdBase}-${idx}-qty`}
            className="h-11 rounded-xl tabular-nums"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label>{L("Delivery frequency", "სიხშირე")}</Label>
          <Select value={line.frequency} onValueChange={(v) => update({ frequency: v })}>
            <SelectTrigger
              className="h-11 rounded-xl"
              data-testid={`${testIdBase}-${idx}-freq-trigger`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {lang === "ka" ? f.ka : f.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export function PartnerBrandLeadDialog({ open, onOpenChange }) {
  const { lang } = useLang();
  const L = (en, ka) => (lang === "ka" ? ka : en);
  const tid = "partner-brand-form";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setName(""); setEmail("");
    setLines([emptyLine()]);
    setNotes(""); setSubmitting(false); setSuccess(false); setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError(L("Name is required", "სახელი აუცილებელია"));
    if (!lines.length || !lines.every((l) => l.product_name.trim()))
      return setError(L("Every product needs a name", "თითოეულ პროდუქტს სჭირდება სახელი"));
    setSubmitting(true);
    try {
      // The backend accepts these fields; email/phone are optional in Phase 4.
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        items: lines.map((l) => ({
          product_name: l.product_name.trim(),
          quantity: Number(l.quantity) || 1,
          frequency: l.frequency,
          unit: "pcs",
        })),
        notes: notes.trim() || null,
      };
      await postJSON("/api/lead/partner-brand", payload);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid={`${tid}-dialog`}
      >
        {success ? (
          <SuccessPanel
            onClose={() => handleOpenChange(false)}
            lang={lang}
            label={tid}
            messageEn="Your request has been received and is being processed. Our team will reach out shortly."
            messageKa="თქვენი შეკითხვა მიღებულია და მუშავდება. ჩვენი გუნდი დაგიკავშირდებათ მოკლე ვადაში."
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {L("Request", "მოთხოვნა")}
              </DialogTitle>
              <DialogDescription>
                {L(
                  "Tell us which products you want and we'll let you know which service package fits you best.",
                  "გვითხარი რა პროდუქტები გსურთ და გეტყვით რომელი მომსახურების პაკეტით შეგიძლიათ ისარგებლოთ.",
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} noValidate className="space-y-5 pt-2">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`${tid}-name`}>{L("Name", "სახელი")}</Label>
                  <Input
                    id={`${tid}-name`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid={`${tid}-name`}
                    className="h-11 rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${tid}-email`}>
                    {L("Email (optional)", "მეილი (არასავალდებულო)")}
                  </Label>
                  <Input
                    id={`${tid}-email`}
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={`${tid}-email`}
                    className="h-11 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {lines.map((line, idx) => (
                  <ProductLineEditor
                    key={idx}
                    line={line}
                    idx={idx}
                    lang={lang}
                    testIdBase={tid}
                    onChange={(next) =>
                      setLines((cur) => cur.map((l, i) => (i === idx ? next : l)))
                    }
                    onRemove={() =>
                      setLines((cur) => cur.filter((_, i) => i !== idx))
                    }
                    canRemove={lines.length > 1}
                  />
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLines((cur) => [...cur, emptyLine()])}
                  className="w-full rounded-xl border-dashed"
                  data-testid={`${tid}-add-line`}
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  {L("Add product", "დაამატე პროდუქტი")}
                </Button>
              </div>

              <div className="space-y-1.5">
                <Label>{L("Additional notes (optional)", "დამატებითი შენიშვნები (არასავალდებულო)")}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid={`${tid}-notes`}
                  className="rounded-xl"
                />
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]"
                  data-testid={`${tid}-error`}
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={`${tid}-submit`}
                  className="rounded-xl bg-[#F25C05] hover:bg-[#d44e02]"
                >
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {L("Send", "გაგზავნა")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Backward-compat alias — the "any brand" variant is no longer used in the launch UI.
export function AnyBrandLeadDialog(props) {
  return <PartnerBrandLeadDialog {...props} />;
}

/* ---------- Consultation dialog ------------------------------------------ */

export function ConsultationLeadDialog({ open, onOpenChange }) {
  const { lang } = useLang();
  const L = (en, ka) => (lang === "ka" ? ka : en);
  const tid = "consultation-form";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setName(""); setPhone(""); setPreferredTime(""); setNotes("");
    setSubmitting(false); setSuccess(false); setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPhoneValid(phone)) return setError(L("Please enter a valid phone", "გთხოვ შეიყვანე ვალიდური ნომერი"));
    if (!preferredTime.trim()) return setError(L("Please tell us when to call", "გვითხარი როდის დაგირეკოთ"));
    setSubmitting(true);
    try {
      await postJSON("/api/lead/consultation", {
        name: name.trim() || null,
        phone,
        preferred_time: preferredTime.trim(),
        notes: notes.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-md"
        data-testid={`${tid}-dialog`}
      >
        {success ? (
          <SuccessPanel
            onClose={() => handleOpenChange(false)}
            lang={lang}
            label={tid}
            messageEn="Your booking has been received. Our expert will call you at the agreed time."
            messageKa="თქვენი ჯავშანი მიღებულია. ჩვენი ექსპერტი დაგიკავშირდებათ დათქმულ დროს."
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {L("Book a consultation", "დაჯავშნე კონსულტაცია")}
              </DialogTitle>
              <DialogDescription>
                {L(
                  "Leave your phone number and when we should call. Our SmartPaw expert will be in touch.",
                  "მოგვწერე შენი ნომერი და დარეკვის სასურველი დრო — ჩვენი SmartPaw ექსპერტი დაგიკავშირდება.",
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} noValidate className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>{L("Your name (optional)", "სახელი (არასავალდებულო)")}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid={`${tid}-name`}
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{L("Phone number", "ტელეფონის ნომერი")}</Label>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  testId={`${tid}-phone`}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>{L("Preferred date & time", "სასურველი თარიღი / დრო")}</Label>
                <Input
                  value={preferredTime}
                  onChange={(e) => setPreferredTime(e.target.value)}
                  placeholder={L("e.g. Tomorrow afternoon, ~14:00", "მაგ. ხვალ, ~14:00")}
                  data-testid={`${tid}-time`}
                  className="h-11 rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>{L("What should we discuss? (optional)", "რის შესახებ ვისაუბროთ? (არასავალდებულო)")}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid={`${tid}-notes`}
                  className="rounded-xl"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]" data-testid={`${tid}-error`}>
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={`${tid}-submit`}
                  className="rounded-xl bg-[#F25C05] hover:bg-[#d44e02]"
                >
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {L("Request a callback", "მოითხოვე ზარი")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
